// src/theme/foundations/palettes.ts
import { defineSemanticTokens, defineTokens } from '@chakra-ui/react'

/**
 * The SIB system palettes, each with one role:
 *
 * - `indigo` — the brand accent. It carries interaction and brand moments, and is
 *   the global colorPalette default, so controls reach it without being told.
 * - `gray` — the system colour: surfaces, lines, text. Harmonised towards indigo
 *   (same hue family, far less chroma), which is the character of the system.
 * - `blue` — informational states. Deliberately cooler and more saturated than
 *   indigo, so an info alert next to a primary button does not read as a defect.
 *
 * The remaining palettes override Chakra's defaults of the same name and carry
 * semantics: green, red, orange, yellow for status; purple, teal, cyan, pink for
 * charts and categories.
 *
 * Feature code must not read these shades directly. Use a `colorPalette` or a
 * semantic token, which resolve to these values centrally. An explicit
 * `colorPalette` belongs only where a call site departs from the brand on
 * purpose.
 */
const shades = {
  // The brand accent.
  indigo: {
    50: 'rgb(245, 246, 250)',
    100: 'rgb(232, 234, 243)',
    200: 'rgb(209, 214, 232)',
    300: 'rgb(175, 184, 211)',
    400: 'rgb(135, 149, 184)',
    500: 'rgb(101, 117, 156)',
    600: 'rgb(77, 91, 128)',
    700: 'rgb(63, 73, 103)',
    800: 'rgb(53, 61, 84)',
    900: 'rgb(44, 50, 68)',
    950: 'rgb(24, 27, 38)',
  },
  // The system colour, harmonised towards indigo with far less chroma.
  gray: {
    50: 'rgb(248, 248, 251)',
    100: 'rgb(236, 237, 242)',
    200: 'rgb(216, 219, 228)',
    300: 'rgb(183, 188, 203)',
    400: 'rgb(138, 144, 163)',
    500: 'rgb(99, 106, 126)',
    600: 'rgb(72, 77, 95)',
    700: 'rgb(56, 60, 74)',
    800: 'rgb(42, 45, 57)',
    900: 'rgb(30, 32, 44)',
    950: 'rgb(16, 17, 27)',
  },
  // Informational states.
  blue: {
    50: 'rgb(239, 246, 255)',
    100: 'rgb(219, 234, 252)',
    200: 'rgb(186, 216, 250)',
    300: 'rgb(134, 188, 246)',
    400: 'rgb(84, 155, 232)',
    500: 'rgb(42, 124, 214)',
    600: 'rgb(29, 100, 180)',
    700: 'rgb(26, 80, 142)',
    800: 'rgb(25, 66, 114)',
    900: 'rgb(23, 56, 94)',
    950: 'rgb(13, 31, 53)',
  },
  green: {
    50: 'rgb(240, 255, 246)',
    100: 'rgb(216, 251, 231)',
    200: 'rgb(179, 244, 207)',
    300: 'rgb(125, 231, 173)',
    400: 'rgb(69, 209, 132)',
    500: 'rgb(34, 181, 104)',
    600: 'rgb(23, 146, 82)',
    700: 'rgb(21, 115, 68)',
    800: 'rgb(21, 92, 57)',
    900: 'rgb(20, 76, 50)',
    950: 'rgb(16, 37, 30)',
  },
  red: {
    50: 'rgb(255, 241, 239)',
    100: 'rgb(255, 220, 214)',
    200: 'rgb(255, 184, 173)',
    300: 'rgb(255, 138, 120)',
    400: 'rgb(249, 93, 72)',
    500: 'rgb(227, 62, 42)',
    600: 'rgb(191, 46, 32)',
    700: 'rgb(152, 41, 31)',
    800: 'rgb(125, 40, 33)',
    900: 'rgb(105, 39, 32)',
    950: 'rgb(53, 21, 20)',
  },
  orange: {
    50: 'rgb(255, 247, 237)',
    100: 'rgb(255, 232, 204)',
    200: 'rgb(255, 208, 154)',
    300: 'rgb(255, 175, 90)',
    400: 'rgb(255, 139, 37)',
    500: 'rgb(233, 111, 18)',
    600: 'rgb(194, 82, 13)',
    700: 'rgb(154, 62, 16)',
    800: 'rgb(124, 52, 20)',
    900: 'rgb(104, 47, 21)',
    950: 'rgb(60, 22, 7)',
  },
  yellow: {
    50: 'rgb(255, 252, 232)',
    100: 'rgb(255, 246, 184)',
    200: 'rgb(255, 234, 118)',
    300: 'rgb(255, 218, 50)',
    400: 'rgb(245, 196, 0)',
    500: 'rgb(217, 166, 0)',
    600: 'rgb(177, 123, 0)',
    700: 'rgb(141, 94, 0)',
    800: 'rgb(117, 76, 5)',
    900: 'rgb(99, 63, 11)',
    950: 'rgb(46, 30, 11)',
  },
  purple: {
    50: 'rgb(247, 242, 255)',
    100: 'rgb(238, 226, 255)',
    200: 'rgb(219, 199, 255)',
    300: 'rgb(192, 158, 255)',
    400: 'rgb(160, 109, 250)',
    500: 'rgb(130, 72, 229)',
    600: 'rgb(106, 52, 194)',
    700: 'rgb(87, 44, 160)',
    800: 'rgb(72, 40, 131)',
    900: 'rgb(62, 36, 110)',
    950: 'rgb(38, 16, 68)',
  },
  teal: {
    50: 'rgb(239, 255, 251)',
    100: 'rgb(207, 255, 244)',
    200: 'rgb(157, 255, 234)',
    300: 'rgb(93, 255, 220)',
    400: 'rgb(29, 240, 201)',
    500: 'rgb(0, 201, 167)',
    600: 'rgb(0, 159, 135)',
    700: 'rgb(6, 125, 108)',
    800: 'rgb(11, 101, 89)',
    900: 'rgb(13, 85, 76)',
    950: 'rgb(16, 38, 37)',
  },
  cyan: {
    50: 'rgb(234, 253, 255)',
    100: 'rgb(201, 248, 255)',
    200: 'rgb(147, 240, 255)',
    300: 'rgb(77, 230, 255)',
    400: 'rgb(19, 215, 242)',
    500: 'rgb(0, 187, 216)',
    600: 'rgb(0, 151, 176)',
    700: 'rgb(8, 118, 138)',
    800: 'rgb(16, 95, 111)',
    900: 'rgb(19, 79, 92)',
    950: 'rgb(6, 51, 61)',
  },
  pink: {
    50: 'rgb(255, 241, 247)',
    100: 'rgb(255, 224, 236)',
    200: 'rgb(255, 193, 218)',
    300: 'rgb(255, 146, 192)',
    400: 'rgb(249, 90, 157)',
    500: 'rgb(232, 50, 131)',
    600: 'rgb(196, 31, 103)',
    700: 'rgb(159, 28, 85)',
    800: 'rgb(131, 28, 73)',
    900: 'rgb(110, 27, 64)',
    950: 'rgb(66, 10, 34)',
  },
} as const

type Shade = keyof (typeof shades)['indigo']

function toTokens(palette: Record<number, string>) {
  return Object.fromEntries(
    Object.entries(palette).map(([shade, value]) => [shade, { value }])
  ) as Record<Shade, { value: string }>
}

export const colorTokens = defineTokens.colors({
  indigo: toTokens(shades.indigo),
  gray: toTokens(shades.gray),
  blue: toTokens(shades.blue),
  green: toTokens(shades.green),
  red: toTokens(shades.red),
  orange: toTokens(shades.orange),
  yellow: toTokens(shades.yellow),
  purple: toTokens(shades.purple),
  teal: toTokens(shades.teal),
  cyan: toTokens(shades.cyan),
  pink: toTokens(shades.pink),
})

/**
 * Role tokens for the palettes this project introduces.
 *
 * Chakra already defines these for its own ramps, and since our values replace
 * its ramp values, `gray` and `blue` pick them up unchanged. Only `indigo` is
 * new and needs its own, plus the focus ring blue takes at shade 400 rather than
 * Chakra's 500.
 */
export const colorSemanticTokens = defineSemanticTokens.colors({
  indigo: {
    contrast: { value: { _light: 'white', _dark: 'white' } },
    fg: { value: { _light: '{colors.indigo.800}', _dark: '{colors.indigo.300}' } },
    subtle: { value: { _light: '{colors.indigo.100}', _dark: '{colors.indigo.900}' } },
    muted: { value: { _light: '{colors.indigo.200}', _dark: '{colors.indigo.800}' } },
    emphasized: { value: { _light: '{colors.indigo.300}', _dark: '{colors.indigo.700}' } },
    solid: { value: { _light: '{colors.indigo.600}', _dark: '{colors.indigo.500}' } },
    focusRing: { value: { _light: '{colors.indigo.400}', _dark: '{colors.indigo.400}' } },
  },
  blue: {
    focusRing: { value: { _light: '{colors.blue.400}', _dark: '{colors.blue.400}' } },
  },
})

export const paletteShades = shades
